// Arena sound triggers (lane C, backlog C-14, C-15; territory-audio.md §3). Pure: arena events
// (as the store queues them, with their arrival time) → sound cues. Recipes are in
// audio/recipes.ts: capture / captureMid / captureBig (gain < 1 %, 1–5 %, ≥ 5 % of the playable
// cells), kill, deathArena, shatter, spill, collect, homeAlarm (setting `arenaAlarm`, default
// off), reveal. `gameSounds` stays default on.
// - A capture that also kills (an enclosure in the same frame) plays capture, then kill at
//   +KILL_AFTER_CAPTURE_MS; a kill in a money room adds moneyGain at +MONEY_GAIN_DELAY_MS.
// - An event older than ARENA_CLIENT.staleEventMs plays nothing (tab return, GC pause, a
//   reconnect burst: C-15, C-23).
// - Not triggered: others' captures, own captures under 0.01 %, deaths out of view.

import { ARENA_CAUSE_CODE, ARENA_CLIENT, ARENA_EVENT, type ArenaEventRecord } from '@ballio/shared';
import type { SoundSlot } from '../audio/recipes.js';
import { MONEY_GAIN_DELAY_MS } from '../audio/triggers.js';

export interface ArenaSoundCue {
  /** An audio/recipes.ts slot name. */
  readonly slot: SoundSlot;
  /** Delay after the event's render time. */
  readonly delayMs: number;
  /** Playback rate (1 = as rendered). */
  readonly rate?: number;
}

/** The kill after a capture in the same frame waits this long, so the two do not mask (§5). */
export const KILL_AFTER_CAPTURE_MS = 60;
/** The home alarm re-arms only after the flag was clear this long (audio row 7). */
export const ALARM_REARM_MS = 3000;

export interface ArenaSoundContext {
  readonly ownId: number;
  /** WELCOME playableCells (the capture tier is a share of it). */
  readonly playableCells: number;
  /** Money or practice room (kills add moneyGain). */
  readonly money: boolean;
  /** Whether a player's head, land or trail is in view (shatter, spill). */
  inView(playerId: number): boolean;
}

/** The capture slot for a gain of `gainedCells`, or null under the label threshold. */
export function captureSlot(gainedCells: number, playableCells: number): SoundSlot | null {
  if (!(playableCells > 0) || !(gainedCells > 0)) return null;
  const pct = (gainedCells / playableCells) * 100;
  if (pct < ARENA_CLIENT.captureLabelMinPct) return null;
  if (pct < 1) return 'capture';
  if (pct < 5) return 'captureMid';
  return 'captureBig';
}

/** The cues for one frame's events (in event order), `ageMs` after their arrival. */
export function classifyArenaEvents(
  events: readonly ArenaEventRecord[],
  ctx: ArenaSoundContext,
  ageMs: number,
): ArenaSoundCue[] {
  const out: ArenaSoundCue[] = [];
  if (ageMs > ARENA_CLIENT.staleEventMs) return out;
  let captured = false;
  for (const e of events) {
    switch (e.type) {
      case ARENA_EVENT.CAPTURE: {
        if (e.playerId !== ctx.ownId || ctx.ownId === 0) break;
        const slot = captureSlot(e.gainedCells, ctx.playableCells);
        if (slot) {
          out.push({ slot, delayMs: 0 });
          captured = true;
        }
        break;
      }
      case ARENA_EVENT.DEATH: {
        if (ctx.ownId !== 0 && e.victimId === ctx.ownId) {
          out.push({ slot: 'deathArena', delayMs: 0 });
        } else if (
          ctx.ownId !== 0 &&
          e.killerId === ctx.ownId &&
          e.cause !== ARENA_CAUSE_CODE.SELF &&
          e.cause !== ARENA_CAUSE_CODE.NONE
        ) {
          const at = captured ? KILL_AFTER_CAPTURE_MS : 0;
          out.push({ slot: 'kill', delayMs: at });
          if (ctx.money) out.push({ slot: 'moneyGain', delayMs: at + MONEY_GAIN_DELAY_MS });
        } else if (ctx.inView(e.victimId)) {
          out.push({ slot: 'shatter', delayMs: 0 });
        }
        break;
      }
      case ARENA_EVENT.SPILL:
        if (e.victimId === ctx.ownId || ctx.inView(e.victimId))
          out.push({ slot: 'spill', delayMs: 0 });
        break;
      case ARENA_EVENT.COLLECT:
        if (ctx.ownId !== 0 && e.playerId === ctx.ownId) out.push({ slot: 'collect', delayMs: 0 });
        break;
      default:
        break;
    }
  }
  return out;
}

/** The cues for one event seen by player `ownId`, `ageMs` after its arrival. */
export function classifyArenaEvent(
  event: ArenaEventRecord,
  ctx: ArenaSoundContext,
  ageMs: number,
): readonly ArenaSoundCue[] {
  return classifyArenaEvents([event], ctx, ageMs);
}

/**
 * The home alarm (audio row 7): fires on the rising edge of the own ALARM flag, re-arming only
 * after the flag has been clear for ALARM_REARM_MS.
 */
export class AlarmSound {
  private high = false;
  private clearSinceMs = Number.NEGATIVE_INFINITY;
  private armed = true;

  /** Feeds the flag at `nowMs`; true when the alarm should sound now. */
  update(flag: boolean, nowMs: number): boolean {
    if (flag) {
      const fire = !this.high && this.armed;
      if (!this.high) this.armed = false;
      this.high = true;
      return fire;
    }
    if (this.high) {
      this.high = false;
      this.clearSinceMs = nowMs;
    }
    if (!this.armed && nowMs - this.clearSinceMs >= ALARM_REARM_MS) this.armed = true;
    return false;
  }

  reset(): void {
    this.high = false;
    this.armed = true;
    this.clearSinceMs = Number.NEGATIVE_INFINITY;
  }
}
